import type { HydraContext, TimeAwareInterface } from "../hydra.interface";

export enum UserPreferencesPropertyNameType {
  Name = "name",
  DisplayName = "displayName",
  Abbreviation = "abbreviation",
}

export enum UserPreferencesViewModeAttachments {
  List = "list",
  Grid = "grid",
}

export enum UserPreferencesTheme {
  System = "system",
  Light = "light",
  Dark = "dark",
}

export type UserPreferencesSyncConcurrencyValue = 1 | 2 | 5 | 10;

export type UserPreferenceScope = "global" | "client" | "location" | "role" | "user";

export type UserPreferenceScopeType = "global" | "client" | "location" | "permissionTemplateGroup" | "user";

export type UserPreferenceSource = "user" | "role" | "location" | "client" | "global" | "default";

export const USER_PREFERENCE_SCOPES: readonly UserPreferenceScope[] = [
  "global",
  "client",
  "location",
  "role",
  "user",
] as const;

export const USER_PREFERENCE_PERSONAL_ONLY_KEYS = [
  "theme",
  "appDoNotAskForPushNotification",
  "webDoNotAskForPushNotification",
  "appKeepScreenAwakeDisabled",
  "appNotificationsEnabled",
  "webNotificationsEnabled",
] as const;

// Views and scope ids differ per location, so a default report view is stored on a location, role or user row only.
export const USER_PREFERENCE_LOCATION_SCOPED_KEYS = [
  "defaultReportView",
] as const;

// The default task list also exists on the organisation, but only without a fixed choice (the API checks that).
export const USER_PREFERENCE_ORGANISATION_DOWN_KEYS = [
  "defaultTaskView",
] as const;

export const USER_PREFERENCE_ORG_MODULE_KEYS = [
  "multipleWarehousesEnabled",
  "inventoryCostsEnabled",
  "enableDescriptionHintModule",
  "enableViewManagementModule",
] as const;

// Modules that apply to a whole organisation at once: switched on the global or organisation row, never per location.
export const USER_PREFERENCE_ORG_WIDE_MODULE_KEYS = [
  "enableAccessRoleModule",
] as const;

export const USER_PREFERENCE_KEYS = [
  "theme",
  "developerMode",
  "debugModeOptimisticUpdates",
  "redirectAfterCreate",
  "showSelectedOptionsSeparate",
  "taskStatusToFinishedAskForComment",
  "appDoNotAskForPushNotification",
  "appNotificationsEnabled",
  "appKeepScreenAwakeDisabled",
  "webNotificationsEnabled",
  "webDoNotAskForPushNotification",
  "enableGlobalMode",
  "onlyShowPropertiesWithOpenTasks",
  "onlyShowPropertiesWithTasksAssignedToMe",
  "onlyShowPropertiesWithOpenReports",
  "showSortAsSubtitleInReportList",
  "enableTimePickerPartitions",
  "syncConcurrency",
  "manualServerVersion",
  "viewModeAttachments",
  "defaultTaskView",
  "defaultReportView",
  "defaultPropertyName",
  "defaultPropertyGroupName",
  "webShowPropertyGroupInTaskList",
  "appShowPropertyGroupInTaskList",
  "webShowPropertyGroupInReportList",
  "appShowPropertyGroupInReportList",
  "webIncludeSubTasks",
  "appIncludeSubTasks",
  "appOrderTasksByGroup",
  "webOrderTasksByGroup",
  "webTaskListShowFinished",
  "webTaskListShowCompleted",
  "webTaskListShowWithoutDeadline",
  "webTaskListShowSearchAndSort",
  "webReportListShowTransferred",
  "webReportListShowClosed",
  "webReportListShowSearchAndSort",
  "appHideQuickFiltersOnTaskList",
  "appHideFiltersOnTaskList",
  "appHideFiltersOnReportList",
  "appHideQuickFiltersOnReportList",
  "notificationsV2Enabled",
  "enableNotificationRecurringDigest",
  "enableNotificationReservations",
  "translateEnabled",
  "enableTaskTimeEntryGlobalView",
  "enableTaskProductGlobalView",
  "enableTaskTotalsGlobalView",
  "enableWalkRouteModule",
  "enableReservationModule",
  "enableAutomationModule",
  "enableAccessRoleModule",
  "enableAiTranslationModule",
  "enableViewManagementModule",
  "enableMultilingualModule",
  "enableBulkTaskAssignee",
  "taskSidebarShowAllUsers",
  "multipleWarehousesEnabled",
  "inventoryCostsEnabled",
  "enableDescriptionHintModule",
  "hideReservationTab",
  "taskViewSwitcherEnabled",
  "reportViewSwitcherEnabled",
] as const;

export type UserPreferenceKey = typeof USER_PREFERENCE_KEYS[number];

const ALL_SCOPES: readonly UserPreferenceScope[] = USER_PREFERENCE_SCOPES;
const PERSONAL_SCOPES: readonly UserPreferenceScope[] = ["global", "user"];
const LOCATION_SCOPED_SCOPES: readonly UserPreferenceScope[] = ["location", "role", "user"];
const ORGANISATION_DOWN_SCOPES: readonly UserPreferenceScope[] = ["client", "location", "role", "user"];
const ORG_MODULE_SCOPES: readonly UserPreferenceScope[] = ["global", "client", "location"];
const ORG_WIDE_MODULE_SCOPES: readonly UserPreferenceScope[] = ["global", "client"];

function buildAllowedScopes(): Record<UserPreferenceKey, readonly UserPreferenceScope[]> {
  const personal = new Set<string>(USER_PREFERENCE_PERSONAL_ONLY_KEYS);
  const locationScoped = new Set<string>(USER_PREFERENCE_LOCATION_SCOPED_KEYS);
  const organisationDown = new Set<string>(USER_PREFERENCE_ORGANISATION_DOWN_KEYS);
  const orgModule = new Set<string>(USER_PREFERENCE_ORG_MODULE_KEYS);
  const orgWideModule = new Set<string>(USER_PREFERENCE_ORG_WIDE_MODULE_KEYS);
  const map = {} as Record<UserPreferenceKey, readonly UserPreferenceScope[]>;
  for (const key of USER_PREFERENCE_KEYS) {
    if (personal.has(key)) {
      map[key] = PERSONAL_SCOPES;
      continue;
    }
    if (locationScoped.has(key)) {
      map[key] = LOCATION_SCOPED_SCOPES;
      continue;
    }
    if (organisationDown.has(key)) {
      map[key] = ORGANISATION_DOWN_SCOPES;
      continue;
    }
    if (orgModule.has(key)) {
      map[key] = ORG_MODULE_SCOPES;
      continue;
    }
    if (orgWideModule.has(key)) {
      map[key] = ORG_WIDE_MODULE_SCOPES;
      continue;
    }
    map[key] = ALL_SCOPES;
  }
  return map;
}

export const USER_PREFERENCE_ALLOWED_SCOPES: Record<UserPreferenceKey, readonly UserPreferenceScope[]> = buildAllowedScopes();

export function scopeTypeToApiScope(scopeType: UserPreferenceScopeType): UserPreferenceScope {
  if (scopeType === "permissionTemplateGroup") {
    return "role";
  }
  return scopeType;
}

export function isUserPreferenceKeyAllowed(key: UserPreferenceKey, scope: UserPreferenceScope): boolean {
  return USER_PREFERENCE_ALLOWED_SCOPES[key].includes(scope);
}

/**
 * Keys a person stores on their row for all locations only, never on the row of one location (the API refuses it and
 * ignores an old value there). The theme follows the person, not the location.
 */
export const USER_PREFERENCE_ALL_LOCATIONS_ONLY_KEYS: readonly UserPreferenceKey[] = ["theme"];

const ALL_LOCATIONS_ONLY_KEYS = new Set<string>(USER_PREFERENCE_ALL_LOCATIONS_ONLY_KEYS);

export function isUserPreferenceKeyForAllLocationsOnly(key: string): boolean {
  return ALL_LOCATIONS_ONLY_KEYS.has(key);
}

export interface TaskListFilterSettings {
  maxFutureDeadline?: string
}

export interface UserPreferences {
  relatedUser: string | null
  client: string | null
  location: string | null
  permissionTemplateGroup: string | null
  scope: UserPreferenceScope
  global: boolean
  sources: Record<string, UserPreferenceSource> | null
  theme: UserPreferencesTheme | null
  developerMode: boolean | null
  debugModeOptimisticUpdates: boolean | null
  redirectAfterCreate: boolean | null
  showSelectedOptionsSeparate: boolean | null
  taskStatusToFinishedAskForComment: boolean | null
  appDoNotAskForPushNotification: boolean | null
  appNotificationsEnabled: boolean | null
  appKeepScreenAwakeDisabled?: boolean | null
  webNotificationsEnabled: boolean | null
  webDoNotAskForPushNotification: boolean | null
  enableGlobalMode: boolean | null
  onlyShowPropertiesWithOpenTasks: boolean | null
  onlyShowPropertiesWithTasksAssignedToMe: boolean | null
  onlyShowPropertiesWithOpenReports: boolean | null
  showSortAsSubtitleInReportList: boolean | null
  enableTimePickerPartitions: boolean | null
  syncConcurrency: UserPreferencesSyncConcurrencyValue | null
  manualServerVersion: string | null
  viewModeAttachments: UserPreferencesViewModeAttachments | null
  /** `<viewKey>` or `<viewKey>@<scopeType>:<id>`, see `DefaultViewReference`. */
  defaultTaskView: string | null
  /** `<viewKey>` or `<viewKey>@<scopeType>:<id>`, see `DefaultViewReference`. */
  defaultReportView: string | null
  defaultPropertyName: UserPreferencesPropertyNameType | null
  defaultPropertyGroupName: UserPreferencesPropertyNameType | null
  webShowPropertyGroupInTaskList: boolean | null
  appShowPropertyGroupInTaskList: boolean | null
  webShowPropertyGroupInReportList: boolean | null
  appShowPropertyGroupInReportList: boolean | null
  webIncludeSubTasks: boolean | null
  appIncludeSubTasks: boolean | null
  appOrderTasksByGroup: boolean | null
  webOrderTasksByGroup: boolean | null
  webTaskListShowFinished: boolean | null
  webTaskListShowCompleted: boolean | null
  webTaskListShowWithoutDeadline: boolean | null
  webTaskListShowSearchAndSort: boolean | null
  webReportListShowTransferred: boolean | null
  webReportListShowClosed: boolean | null
  webReportListShowSearchAndSort: boolean | null
  appHideQuickFiltersOnTaskList: boolean | null
  appHideFiltersOnTaskList: boolean | null
  appHideFiltersOnReportList: boolean | null
  appHideQuickFiltersOnReportList: boolean | null
  notificationsV2Enabled: boolean | null
  enableNotificationRecurringDigest: boolean | null
  enableNotificationReservations: boolean | null
  translateEnabled: boolean | null
  enableTaskTimeEntryGlobalView: boolean | null
  enableTaskProductGlobalView: boolean | null
  enableTaskTotalsGlobalView: boolean | null
  enableWalkRouteModule: boolean | null
  enableReservationModule: boolean | null
  enableAutomationModule: boolean | null
  enableAccessRoleModule: boolean | null
  enableAiTranslationModule: boolean | null
  enableViewManagementModule: boolean | null
  enableMultilingualModule: boolean | null
  enableDescriptionHintModule: boolean | null
  enableBulkTaskAssignee: boolean | null
  taskSidebarShowAllUsers: boolean | null
  multipleWarehousesEnabled: boolean | null
  inventoryCostsEnabled: boolean | null
  hideReservationTab: boolean | null
  /** Whether Tasks shows the list switcher (model A, view management on). Off: Tasks opens on the default list. */
  taskViewSwitcherEnabled: boolean | null
  /** Whether Reports shows the list switcher (model A, view management on). Off: Reports opens on the default list. */
  reportViewSwitcherEnabled: boolean | null
  taskListFilters?: Record<string, TaskListFilterSettings>
}

export interface UserPreferencesHydraItem extends UserPreferences, HydraContext, TimeAwareInterface {
  id: number
}

/** A level of the resolution chain, highest first: who it belongs to and the values it sets itself. */
export type UserPreferenceResolutionLevel = "user_location" | "user" | "role_location" | "role_client" | "location" | "client" | "global" | "default";

export interface UserPreferenceLayer {
  level: UserPreferenceResolutionLevel
  source: UserPreferenceSource
  /** The user, role, location or organisation the level belongs to; null for the Verbleif-wide levels. */
  name: string | null
  /** Only the keys this level sets. The `default` level holds every key. */
  preferences: Partial<Record<UserPreferenceKey, unknown>>
}

/** The inherited values of a scope (`/api/user_preferences/inherited`) with the resolution chain behind them. */
export interface UserPreferencesInheritedHydraItem extends UserPreferencesHydraItem {
  layers: UserPreferenceLayer[]
}

export interface UserPreferencesWrite extends Partial<Omit<UserPreferences, "relatedUser" | "client" | "location" | "permissionTemplateGroup" | "scope" | "global" | "sources">> {
  relatedUser?: string | null
  client?: string | null
  location?: string | null
  permissionTemplateGroup?: string | null
  scope?: UserPreferenceScope
  global?: boolean
}

export interface UserPreferencesPatch extends Partial<Omit<UserPreferences, "relatedUser" | "client" | "location" | "permissionTemplateGroup" | "scope" | "global" | "sources">> {}
